import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  adminList,
  adminCreate,
  adminUpdate,
  adminDelete,
  adminSubscribers,
  adminOrders,
  adminMe,
  adminGenerateImage,
  clearToken,
} from "@/lib/admin";

const CONTENT_KINDS = [
  { key: "articles", label: "Journal", titleField: "title" },
  { key: "rituals", label: "Rituals", titleField: "title" },
  { key: "products", label: "Products", titleField: "name" },
  { key: "ingredients", label: "Library", titleField: "name" },
];

const READ_ONLY_KINDS = [
  { key: "subscribers", label: "Subscribers" },
  { key: "orders", label: "Orders" },
];

const IMAGE_TARGET_TYPE = {
  articles: "article",
  rituals: "ritual",
  products: "product",
};

const STYLE_SUFFIX =
  " — editorial wellness still life, cinematic warm morning light, soft shadows, natural linen and ceramic textures, muted bone-clay-sand palette with quiet moss accents, slow luxury minimalism, shot on medium-format film, shallow depth of field, no people, no text.";

function defaultPromptFor(kind, doc) {
  if (!doc) return "";
  if (kind === "articles") {
    return [doc.title, doc.subtitle, doc.excerpt].filter(Boolean).join(" — ") + STYLE_SUFFIX;
  }
  if (kind === "rituals") {
    return [doc.title, doc.summary].filter(Boolean).join(" — ") + STYLE_SUFFIX;
  }
  if (kind === "products") {
    return [doc.name, doc.description].filter(Boolean).join(" — ") + STYLE_SUFFIX;
  }
  return "";
}

const EMPTY_TEMPLATES = {
  articles: {
    slug: "new-article",
    title: "",
    subtitle: "",
    category: "",
    read_time: "",
    author: "",
    published: "",
    cover_image: "",
    excerpt: "",
    body: [""],
    tags: [],
  },
  rituals: {
    slug: "new-ritual",
    title: "",
    duration: "",
    image_url: "",
    summary: "",
    body: [""],
    core_ritual: "",
  },
  products: {
    slug: "new-product",
    name: "",
    price: 0,
    currency: "INR",
    image_url: "",
    description: "",
    collection: "the-rest-ritual",
    stage: "",
    sequence: 99,
    is_hero: false,
  },
  ingredients: {
    slug: "new-ingredient",
    name: "",
    family: "",
    summary: "",
    body: "",
  },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeKind, setActiveKind] = useState("articles");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // currently editing doc or null
  const [draft, setDraft] = useState(""); // JSON string in editor
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imgPrompt, setImgPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showImgPanel, setShowImgPanel] = useState(false);

  // Fetch current admin once
  useEffect(() => {
    adminMe()
      .then(setUser)
      .catch(() => {
        clearToken();
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  // Load list whenever tab changes
  useEffect(() => {
    let active = true;
    setSelected(null);
    setIsNew(false);
    setItems([]);
    setLoading(true);
    const loader =
      activeKind === "subscribers"
        ? adminSubscribers().then((d) => d.subscribers)
        : activeKind === "orders"
        ? adminOrders().then((d) => d.orders)
        : adminList(activeKind);
    loader
      .then((rows) => active && setItems(rows || []))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Failed to load.";
        toast.error(msg);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [activeKind]);

  const isContent = useMemo(
    () => CONTENT_KINDS.some((k) => k.key === activeKind),
    [activeKind]
  );
  const titleField =
    CONTENT_KINDS.find((k) => k.key === activeKind)?.titleField || "title";

  const onLogout = () => {
    clearToken();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const openEdit = (doc) => {
    setIsNew(false);
    setSelected(doc);
    setDraft(JSON.stringify(doc, null, 2));
    setShowImgPanel(false);
    setImgPrompt(defaultPromptFor(activeKind, doc));
  };

  const openNew = () => {
    const tmpl = EMPTY_TEMPLATES[activeKind] || { slug: "" };
    setIsNew(true);
    setSelected(tmpl);
    setDraft(JSON.stringify(tmpl, null, 2));
    setShowImgPanel(false);
    setImgPrompt("");
  };

  const onSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      toast.error("Invalid JSON.");
      return;
    }
    if (!parsed.slug) {
      toast.error("slug is required.");
      return;
    }
    setSaving(true);
    try {
      const saved = isNew
        ? await adminCreate(activeKind, parsed)
        : await adminUpdate(activeKind, selected.slug, parsed);
      toast.success(isNew ? "Created." : "Saved.");
      // Refresh list
      const rows = await adminList(activeKind);
      setItems(rows);
      setSelected(saved);
      setIsNew(false);
      setDraft(JSON.stringify(saved, null, 2));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onGenerate = async () => {
    if (!selected) return;
    const targetType = IMAGE_TARGET_TYPE[activeKind];
    if (!targetType) {
      toast.error("Image generation isn't supported for this content type.");
      return;
    }
    const prompt = imgPrompt.trim();
    if (prompt.length < 12) {
      toast.error("Add a more descriptive prompt.");
      return;
    }
    setGenerating(true);
    try {
      const res = await adminGenerateImage({
        prompt,
        target_type: targetType,
        target_slug: isNew ? null : selected.slug,
      });
      let current;
      try {
        current = JSON.parse(draft);
      } catch {
        current = { ...selected };
      }
      current[res.target_field] = res.url;
      setSelected(current);
      setDraft(JSON.stringify(current, null, 2));
      if (!isNew) {
        const rows = await adminList(activeKind);
        setItems(rows);
      }
      toast.success("Cover regenerated.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Image generation failed.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async () => {
    if (!selected || isNew) return;
    if (!window.confirm(`Delete "${selected.slug}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await adminDelete(activeKind, selected.slug);
      toast.success("Deleted.");
      const rows = await adminList(activeKind);
      setItems(rows);
      setSelected(null);
      setDraft("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Delete failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone text-ink flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-hairline bg-white min-h-screen sticky top-0 self-start">
        <div className="px-6 py-8 border-b border-hairline">
          <div className="font-display text-2xl tracking-[-0.02em]">
            still <span className="italic font-light">form</span>
          </div>
          <div className="mt-1 text-[0.62rem] tracking-[0.28em] uppercase text-charcoal">
            Admin
          </div>
        </div>

        <nav className="py-6">
          <div className="px-6 py-2 text-[0.62rem] tracking-[0.28em] uppercase text-charcoal">
            Content
          </div>
          {CONTENT_KINDS.map((k) => (
            <button
              key={k.key}
              onClick={() => setActiveKind(k.key)}
              data-testid={`admin-tab-${k.key}`}
              className={`w-full text-left px-6 py-2.5 text-[0.78rem] tracking-[0.12em] uppercase border-l-2 transition-colors ${
                activeKind === k.key
                  ? "border-ink text-ink bg-bone"
                  : "border-transparent text-charcoal hover:text-ink"
              }`}
            >
              {k.label}
            </button>
          ))}

          <div className="px-6 py-2 mt-6 text-[0.62rem] tracking-[0.28em] uppercase text-charcoal">
            Reports
          </div>
          {READ_ONLY_KINDS.map((k) => (
            <button
              key={k.key}
              onClick={() => setActiveKind(k.key)}
              data-testid={`admin-tab-${k.key}`}
              className={`w-full text-left px-6 py-2.5 text-[0.78rem] tracking-[0.12em] uppercase border-l-2 transition-colors ${
                activeKind === k.key
                  ? "border-ink text-ink bg-bone"
                  : "border-transparent text-charcoal hover:text-ink"
              }`}
            >
              {k.label}
            </button>
          ))}
        </nav>

        <div className="px-6 py-6 border-t border-hairline mt-auto">
          {user && (
            <div className="text-[0.7rem] text-charcoal break-all mb-4" data-testid="admin-user-email">
              {user.email}
            </div>
          )}
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="text-[0.72rem] tracking-[0.22em] uppercase text-charcoal hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main area */}
      <main className="flex-1 flex">
        {/* List column */}
        <section className="w-[420px] border-r border-hairline bg-bone">
          <div className="px-8 py-6 flex items-center justify-between border-b border-hairline">
            <h2 className="font-display text-2xl tracking-[-0.01em] capitalize">
              {activeKind}
            </h2>
            {isContent && (
              <button
                onClick={openNew}
                data-testid="admin-new"
                className="text-[0.7rem] tracking-[0.24em] uppercase bg-ink text-bone px-4 py-2 hover:bg-charcoal transition-colors"
              >
                + New
              </button>
            )}
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 84px)" }}>
            {loading ? (
              <div className="px-8 py-12 text-[0.7rem] tracking-[0.24em] uppercase text-charcoal">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="px-8 py-12 text-[0.7rem] tracking-[0.24em] uppercase text-charcoal" data-testid="admin-empty">
                No items.
              </div>
            ) : isContent ? (
              <ul data-testid="admin-list">
                {items.map((doc) => (
                  <li key={doc.slug || doc.id}>
                    <button
                      onClick={() => openEdit(doc)}
                      data-testid={`admin-item-${doc.slug}`}
                      className={`w-full text-left px-8 py-4 border-b border-hairline-subtle hover:bg-white transition-colors ${
                        selected?.slug === doc.slug ? "bg-white" : ""
                      }`}
                    >
                      <div className="text-[0.92rem] text-ink leading-tight">
                        {doc[titleField] || doc.slug}
                      </div>
                      <div className="text-[0.65rem] tracking-[0.18em] uppercase text-charcoal mt-1 font-mono">
                        {doc.slug}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <ul data-testid="admin-list">
                {items.map((row, i) => (
                  <li
                    key={i}
                    className="px-8 py-4 border-b border-hairline-subtle"
                    data-testid={`admin-row-${i}`}
                  >
                    {activeKind === "subscribers" ? (
                      <>
                        <div className="text-[0.92rem] text-ink">{row.email}</div>
                        <div className="text-[0.65rem] tracking-[0.18em] uppercase text-charcoal mt-1 font-mono">
                          {row.subscribed_at}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[0.92rem] text-ink">
                          {row.customer_email || "—"} · {row.currency?.toUpperCase()} {(row.amount || 0).toFixed(2)}
                        </div>
                        <div className="text-[0.65rem] tracking-[0.18em] uppercase text-charcoal mt-1 font-mono">
                          {row.payment_status} · {row.session_id?.slice(0, 20)}…
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Editor */}
        <section className="flex-1 bg-white" data-testid="admin-editor">
          {!isContent ? (
            <div className="px-12 py-16 text-charcoal text-[0.78rem] tracking-[0.18em] uppercase">
              Read-only report
            </div>
          ) : !selected ? (
            <div className="px-12 py-16 text-charcoal text-[0.78rem] tracking-[0.18em] uppercase" data-testid="admin-editor-empty">
              Select an item to edit, or create a new one.
            </div>
          ) : (
            <div className="px-12 py-10 max-w-3xl">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-[0.62rem] tracking-[0.28em] uppercase text-charcoal mb-2">
                    {isNew ? "New" : "Editing"} · {activeKind}
                  </div>
                  <h3 className="font-display text-3xl tracking-[-0.01em]">
                    {selected[titleField] || selected.slug || "Untitled"}
                  </h3>
                </div>
                <div className="flex gap-3">
                  {IMAGE_TARGET_TYPE[activeKind] && (
                    <button
                      onClick={() => setShowImgPanel((v) => !v)}
                      disabled={generating}
                      data-testid="admin-toggle-imggen"
                      className="text-[0.7rem] tracking-[0.24em] uppercase border border-hairline text-charcoal px-4 py-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-50"
                    >
                      {showImgPanel ? "Hide cover" : "Cover image"}
                    </button>
                  )}
                  {!isNew && (
                    <button
                      onClick={onDelete}
                      disabled={saving}
                      data-testid="admin-delete"
                      className="text-[0.7rem] tracking-[0.24em] uppercase border border-hairline text-charcoal px-4 py-2 hover:border-ink hover:text-ink transition-colors disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={onSave}
                    disabled={saving}
                    data-testid="admin-save"
                    className="text-[0.7rem] tracking-[0.24em] uppercase bg-ink text-bone px-5 py-2 hover:bg-charcoal transition-colors disabled:opacity-50"
                  >
                    {saving ? "Saving…" : isNew ? "Create" : "Save"}
                  </button>
                </div>
              </div>

              {showImgPanel && IMAGE_TARGET_TYPE[activeKind] && (
                <div className="mb-8 border border-hairline bg-bone p-6" data-testid="admin-imggen-panel">
                  <div className="flex items-start gap-6">
                    {(() => {
                      const field = activeKind === "rituals" ? "image_url" : "cover_image";
                      const url = (() => {
                        try {
                          return JSON.parse(draft)[field];
                        } catch {
                          return selected[field];
                        }
                      })();
                      return url ? (
                        <img
                          src={url}
                          alt="Current cover"
                          data-testid="admin-current-cover"
                          className="w-40 h-40 object-cover border border-hairline"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-40 h-40 border border-hairline flex items-center justify-center text-[0.62rem] tracking-[0.22em] uppercase text-charcoal bg-white">
                          No cover
                        </div>
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <label className="block text-[0.62rem] tracking-[0.28em] uppercase text-charcoal mb-2">
                        Image prompt
                      </label>
                      <textarea
                        value={imgPrompt}
                        onChange={(e) => setImgPrompt(e.target.value)}
                        data-testid="admin-imggen-prompt"
                        rows={4}
                        className="w-full text-[0.82rem] leading-relaxed bg-white border border-hairline p-3 outline-none focus:border-ink resize-y"
                      />
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          onClick={onGenerate}
                          disabled={generating || !selected?.slug}
                          data-testid="admin-imggen-submit"
                          className="text-[0.7rem] tracking-[0.24em] uppercase bg-ink text-bone px-5 py-2 hover:bg-charcoal transition-colors disabled:opacity-50"
                        >
                          {generating ? "Generating…" : "Regenerate cover"}
                        </button>
                        <span className="text-[0.62rem] tracking-[0.22em] uppercase text-charcoal">
                          Gemini · Nano Banana · ~15–30s
                        </span>
                      </div>
                      <p className="mt-3 text-[0.65rem] tracking-[0.18em] uppercase text-charcoal">
                        Updates {activeKind === "rituals" ? "image_url" : "cover_image"} on save.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <label className="block text-[0.62rem] tracking-[0.28em] uppercase text-charcoal mb-2">
                Document (JSON)
              </label>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                data-testid="admin-json-editor"
                className="w-full font-mono text-[0.82rem] leading-relaxed bg-bone border border-hairline p-5 outline-none focus:border-ink min-h-[60vh] resize-y"
              />
              <p className="mt-3 text-[0.65rem] tracking-[0.18em] uppercase text-charcoal">
                Tip · Body fields accept an array of paragraphs. Tags accept an array of strings.
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
